import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Heart, ShareNetwork, SpeakerHigh, SpeakerSlash, VideoCamera, Plus, PencilSimple, Trash, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import VideoEmbed from '@/components/VideoEmbed';
import RssArticle from '@/components/RssArticle';
import LeaderboardCard from '@/components/LeaderboardCard';
import NotificationBell from '@/components/NotificationBell';
import VenmoDonate from '@/components/VenmoDonate';

function FeedItem({ post, onLike, onDelete, onEdit, userId, isAdmin }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editCaption, setEditCaption] = useState(post.caption || '');
  const navigate = useNavigate();

  const isOwner = userId === post.user_id;
  const canModify = isOwner || isAdmin;

  // Same stable-visibility debounce pattern as VideoEmbed (prevents
  // play()/pause() cascade during fast scroll-snap travel).
  useEffect(() => {
    if (!containerRef.current) return;
    let stableTimer = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          stableTimer = setTimeout(() => {
            setLoaded(true);
            if (post.media_type === 'video') videoRef.current?.play().catch(() => {});
          }, 350);
        } else {
          if (stableTimer) {
            clearTimeout(stableTimer);
            stableTimer = null;
          }
          if (post.media_type === 'video') videoRef.current?.pause();
        }
      },
      { threshold: [0, 0.6, 0.9] }
    );
    observer.observe(containerRef.current);
    return () => {
      if (stableTimer) clearTimeout(stableTimer);
      observer.disconnect();
    };
  }, [post.media_type]);

  const isLiked = (post.liked_by || []).includes(userId);
  const mediaUrl = `${API_BASE}/api/media/${post.media_path}`;

  const handleShare = async () => {
    const url = `${window.location.origin}/post/${post.post_id}`;
    try { await navigator.clipboard.writeText(url); toast.success('Link copied!'); }
    catch { toast.error('Could not copy link'); }
  };

  const handleSaveEdit = async () => {
    try {
      await onEdit(post.post_id, { caption: editCaption });
      setEditing(false);
      toast.success('Post updated');
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this post?')) return;
    try { await onDelete(post.post_id); toast.success('Post deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div ref={containerRef} className="feed-item" data-testid={`feed-post-${post.post_id}`}>
      {loaded && post.media_type === 'video' ? (
        <video
          ref={videoRef}
          src={mediaUrl}
          className="absolute inset-0 w-full h-full object-cover"
          muted={muted}
          loop
          playsInline
          onClick={() => setMuted(!muted)}
        />
      ) : loaded && post.media_type === 'image' ? (
        <img src={mediaUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      {post.media_type === 'video' && (
        <button
          onClick={() => setMuted(!muted)}
          className="absolute top-4 right-4 glass-pill rounded-full p-2 text-white z-10"
          data-testid={`feed-mute-${post.post_id}`}
        >
          {muted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
        </button>
      )}
      <div className="feed-overlay">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate(`/profile/${post.user_id}`)}
            className="text-sm font-sans font-semibold text-white hover:underline"
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
            data-testid={`post-username-${post.post_id}`}
          >
            @{post.user_name || 'golfer'}
          </button>
          {post.tags?.map(tag => (
            <span key={tag} className="text-xs text-white/50 font-sans">#{tag}</span>
          ))}
        </div>
        {editing ? (
          <div className="flex gap-1">
            <input value={editCaption} onChange={e => setEditCaption(e.target.value)} className="flex-1 bg-white/10 text-white text-xs p-1 rounded" />
            <button onClick={handleSaveEdit} className="text-green-400"><Check size={16} /></button>
            <button onClick={() => setEditing(false)} className="text-red-400"><X size={16} /></button>
          </div>
        ) : (
          <p className="text-white text-sm font-sans" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{post.caption}</p>
        )}
        <div className="flex items-center gap-4 mt-2">
          <button onClick={() => onLike(post.post_id)} className="flex items-center gap-1 text-white" data-testid={`post-like-${post.post_id}`}>
            <Heart size={18} weight={isLiked ? 'fill' : 'regular'} className={isLiked ? 'text-red-500' : ''} />
            <span className="text-xs font-sans">{post.likes_count || 0}</span>
          </button>
          <button onClick={handleShare} className="text-white"><ShareNetwork size={18} /></button>
          {canModify && (
            <>
              <button onClick={() => setEditing(true)} className="text-white/60 hover:text-white"><PencilSimple size={16} /></button>
              <button onClick={handleDelete} className="text-white/60 hover:text-red-400"><Trash size={16} /></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyFeed() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] px-6 text-center" data-testid="empty-feed">
      <VideoCamera size={48} className="text-muted-foreground/30 mb-4" />
      <h3 className="text-xl font-serif font-semibold text-foreground mb-1">The Fairway Awaits</h3>
      <p className="text-muted-foreground text-sm font-sans mb-6 max-w-[240px]">No content yet. Share your first round or explore the community.</p>
      <button onClick={() => navigate('/auth')} className="bg-primary text-primary-foreground px-6 py-2.5 rounded-sm text-sm font-sans font-semibold" data-testid="empty-feed-join-btn">JOIN THE CLUB</button>
    </div>
  );
}

export default function FeedPage() {
  const [posts, setPosts] = useState([]);
  const [curatedVideos, setCuratedVideos] = useState([]);
  const [rssArticles, setRssArticles] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [prefs, setPrefs] = useState({ show_videos: true, show_youtube: true, show_rss: true, rss_sources: {} });
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  const fetchPosts = useCallback(async (skip = 0) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const { data } = await api.get(`/posts?skip=${skip}&limit=10`);
      if (skip === 0) setPosts(data.posts);
      else setPosts(prev => [...prev, ...data.posts]);
      setHasMore(skip + data.posts.length < data.total);
    } catch (err) { console.error('Feed fetch error:', err); }
    finally { setLoading(false); loadingRef.current = false; }
  }, []);

  const fetchCuratedVideos = useCallback(async () => {
    try {
      const { data } = await api.get('/videos/curated?limit=60&shuffle=true');
      setCuratedVideos(data.videos || []);
    } catch (err) { console.error('Curated videos fetch error:', err); }
  }, []);

  const fetchRssArticles = useCallback(async () => {
    try {
      let sourcesParam = '';
      if (prefs.rss_sources) {
        const enabledKeys = Object.entries(prefs.rss_sources)
          .filter(([, v]) => v)
          .map(([k]) => k);
        if (enabledKeys.length > 0) sourcesParam = `&sources=${enabledKeys.join(',')}`;
      }
      const { data } = await api.get(`/rss/articles?limit=20${sourcesParam}`);
      setRssArticles(data.articles || []);
    }
    catch (err) { console.error('RSS fetch error:', err); }
  }, [prefs.rss_sources]);

  const fetchLeaderboard = useCallback(async () => {
    try { const { data } = await api.get('/leaderboard'); setLeaderboard(data); }
    catch (err) { console.error('Leaderboard fetch error:', err); }
  }, []);

  const fetchPrefs = useCallback(async () => {
    if (!user) return;
    try { const { data } = await api.get('/users/me/feed-preferences'); setPrefs(data); }
    catch {}
  }, [user]);

  useEffect(() => {
    fetchPosts(0);
    fetchCuratedVideos();
    fetchRssArticles();
    fetchLeaderboard();
    fetchPrefs();
  }, [fetchPosts, fetchCuratedVideos, fetchRssArticles, fetchLeaderboard, fetchPrefs]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore) fetchPosts(posts.length);
    }, { threshold: 0.1, rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [posts.length, hasMore, fetchPosts]);

  const handleLike = async (postId) => {
    if (!user) { navigate('/auth'); return; }
    try {
      const { data } = await api.post(`/posts/${postId}/like`);
      setPosts(prev => prev.map(p => {
        if (p.post_id !== postId) return p;
        return {
          ...p,
          likes_count: data.liked ? p.likes_count + 1 : p.likes_count - 1,
          liked_by: data.liked ? [...(p.liked_by || []), user.user_id] : (p.liked_by || []).filter(id => id !== user.user_id)
        };
      }));
    } catch {}
  };

  const handleDelete = async (postId) => {
    await api.delete(`/posts/${postId}`);
    setPosts(prev => prev.filter(p => p.post_id !== postId));
  };

  const handleEdit = async (postId, updates) => {
    const { data } = await api.put(`/posts/${postId}`, updates);
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, ...data } : p));
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="animate-pulse space-y-2 text-center">
          <div className="w-12 h-12 rounded-full bg-muted mx-auto" />
          <div className="w-24 h-3 bg-muted rounded mx-auto" />
        </div>
      </div>
    );
  }

  // ============ FEED MIXER: 1 RSS article → 3 videos, with leaderboard inserted ============
  const feedItems = [];
  let vidIdx = 0;
  let rssIdx = 0;
  let lbInserted = false;

  // Insert leaderboard at top if available
  if (leaderboard?.tournaments?.length) {
    feedItems.push({ type: 'leaderboard', data: leaderboard, key: 'leaderboard-feed' });
    lbInserted = true;
  }

  // Insert user posts first (if any), then start the RSS→Videos pattern
  if (prefs.show_videos && posts.length > 0) {
    posts.forEach((post) => {
      feedItems.push({ type: 'post', data: post, key: post.post_id });
    });
  }

  // Main feed pattern: 1 RSS article → 3 curated videos (repeat)
  while (rssIdx < rssArticles.length || vidIdx < curatedVideos.length) {
    // 1 RSS article
    if (prefs.show_rss && rssIdx < rssArticles.length) {
      feedItems.push({ type: 'rss', data: rssArticles[rssIdx], key: `rss-${rssArticles[rssIdx].article_id}` });
      rssIdx++;
    }
    // 3 curated videos
    if (prefs.show_youtube) {
      for (let v = 0; v < 3 && vidIdx < curatedVideos.length; v++) {
        feedItems.push({ type: 'video', data: curatedVideos[vidIdx], key: `vid-${curatedVideos[vidIdx].video_id}-${vidIdx}` });
        vidIdx++;
      }
    }
    // Safety: if no RSS left and no videos left, break
    if ((!prefs.show_rss || rssIdx >= rssArticles.length) && (!prefs.show_youtube || vidIdx >= curatedVideos.length)) break;
  }

  if (!feedItems.length) return <EmptyFeed />;

  return (
    <div className="feed-container relative" data-testid="feed-container">
      {user && (
        <div className="fixed top-14 right-3 z-40 lg:hidden" data-testid="mobile-notification-bell">
          <NotificationBell />
        </div>
      )}

      {feedItems.map(item => {
        if (item.type === 'post') return <FeedItem key={item.key} post={item.data} onLike={handleLike} onDelete={handleDelete} onEdit={handleEdit} userId={user?.user_id} isAdmin={user?.role === 'admin'} />;
        if (item.type === 'video') return <VideoEmbed key={item.key} video={item.data} />;
        if (item.type === 'rss') return <RssArticle key={item.key} article={item.data} />;
        if (item.type === 'leaderboard') return <LeaderboardCard key={item.key} data={item.data} />;
        return null;
      })}

      {hasMore && <div ref={sentinelRef} className="h-20 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}

      {user && (
        <button
          onClick={() => navigate('/upload')}
          className="fixed bottom-20 right-4 z-40 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg hover:bg-[#083313] transition-colors"
          data-testid="feed-upload-fab"
        >
          <Plus size={22} weight="bold" />
        </button>
      )}

      <div className="fixed bottom-36 left-4 z-40 lg:hidden" data-testid="feed-venmo-mobile">
        <VenmoDonate compact />
      </div>
    </div>
  );
}
